export const productMatchesTags = (productTags: string[], tags: string[]) => {
  return productTags.some((tag) => tags.includes(tag.toLowerCase()));
};

export function itemInInvalidCollection(
  itemCollections: string[],
  invalidCollections: string[],
): boolean {
  return itemCollections.some((collection) =>
    invalidCollections.includes(collection),
  );
}

export function listsHaveMatchingElement(list1: string[], list2: string[]) {
  return list1.some((key) =>
    list2.some(
      (attribute) =>
        attribute.toLowerCase().trim() === key.toLowerCase().trim(),
    ),
  );
}

/**
 * Returns the exclusion lists for returns coverage.
 *
 * If final sale returns coverage is enabled, then we exclude items that are eligible for final sale returns coverage.
 * This is because and item cannot be eligible for both returns coverage and final sale returns coverage.
 *
 * @param returnsCoverage - The exclusions for returns coverage.
 * @param finalSaleReturnsCoverage - The inclusions for final sale returns coverage.
 * @returns The exclusions for returns coverage.
 */
export function getReturnsCoverageExclusions({
  returnsCoverage,
  finalSaleReturnsCoverage,
}: {
  returnsCoverage: {
    coverageExcludeProperties: string[];
    coverageExcludeCollections: string[];
    invalidProductTags: string[];
  };
  finalSaleReturnsCoverage: {
    coverageEnabled: boolean;
    validProductTags: string[];
    validCollectionTitles: string[];
  };
}): {
  coverageExcludeProperties: string[];
  coverageExcludeCollections: string[];
  invalidProductTags: string[];
} {
  const coverageExclusions = {
    coverageExcludeProperties: returnsCoverage.coverageExcludeProperties,
    coverageExcludeCollections: returnsCoverage.coverageExcludeCollections,
    invalidProductTags: returnsCoverage.invalidProductTags,
  };
  if (finalSaleReturnsCoverage.coverageEnabled) {
    coverageExclusions.invalidProductTags =
      coverageExclusions.invalidProductTags
        .concat(finalSaleReturnsCoverage.validProductTags)
        .filter(Boolean);
    coverageExclusions.coverageExcludeCollections =
      coverageExclusions.coverageExcludeCollections
        .concat(finalSaleReturnsCoverage.validCollectionTitles)
        .filter(Boolean);
  }
  return coverageExclusions;
}

export function itemValidForReturns({
  coverageExcludeProperties,
  coverageExcludeCollections,
  itemInfo,
  invalidProductTags,
}: {
  coverageExcludeProperties: string[];
  coverageExcludeCollections: string[];
  itemInfo: {
    properties: string[];
    productTags: string[];
    collections: string[];
  };
  invalidProductTags: string[];
}): boolean {
  const hasInvalidCollection = listsHaveMatchingElement(
    itemInfo.collections,
    coverageExcludeCollections,
  );
  const hasInvalidProperties = listsHaveMatchingElement(
    itemInfo.properties,
    coverageExcludeProperties,
  );
  const hasInvalidTags = listsHaveMatchingElement(
    itemInfo.productTags,
    invalidProductTags,
  );

  return !hasInvalidTags && !hasInvalidCollection && !hasInvalidProperties;
}

export function itemValidForProtection({
  itemInfo,
  invalidProductTags,
}: {
  itemInfo: {
    productTags: string[];
    // If you need to exclude package protection on collections or properties, you should make these parameters required
    collections?: string[];
    properties?: string[];
  };
  invalidProductTags: string[];
}): boolean {
  const hasInvalidTags = listsHaveMatchingElement(
    itemInfo.productTags,
    invalidProductTags,
  );

  return !hasInvalidTags;
}

export function itemValidForFinalSaleReturns({
  itemInfo,
  validProductTags,
  validCollections,
}: {
  itemInfo: { productTags: string[]; collections: string[] };
  validProductTags: string[];
  validCollections: string[];
}): boolean {
  const hasValidTags = listsHaveMatchingElement(
    itemInfo.productTags,
    validProductTags,
  );

  const hasValidCollections = listsHaveMatchingElement(
    itemInfo.collections,
    validCollections,
  );

  return hasValidTags || hasValidCollections;
}
